import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserConfiguration, UserPreferences } from 'estensa-module';
import { merge } from 'lodash-es';
import { Observable, defer, firstValueFrom } from 'rxjs';
import { CacheService } from '../../utils/cache.service';
import { ServerRootService } from '../../utils/routing/serverroot.service';
import { DeepPartial } from '../../utils/types';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '../notification.service';

export const ESTENSA_MODULE_USER_CONFIGURATION_SETTINGS_SERVICE = new InjectionToken(
    'estensa.module.user-configuration-settings.service',
);

export interface UserConfigurationSettingsServiceNg1 {
    getUserConfigurationAsJSON(): Promise<UserConfiguration>;
    setDefaultConfigurationUserPreferences(prefs: DeepPartial<UserPreferences>): Promise<UserConfiguration>;
    setUserPreferences(prefs: DeepPartial<UserPreferences>): Promise<UserConfiguration>;
}

export class NullUserConfigurationSettingsService {
    getUserConfigurationAsJSON() {
        return Promise.resolve({
            authorizations: {},
            preferences: {
                adminPagesUserPreference: {
                    execution: {
                        filters: '[]',
                        toolbar: '[]',
                    },
                },
            },
        });
    }

    async setDefaultConfigurationUserPreferences() {
        /* This is intentional */
    }

    async setUserPreferences() {
        /* This is intentional */
    }
}

@Injectable({
    providedIn: 'root',
})
export class UserConfigurationSettingsService extends ServerRootService {
    // #region Properties

    private _userConfiguration: UserConfiguration | null = null;
    get userConfiguration() {
        return this._userConfiguration;
    }

    get userConfigurationAsync() {
        return this.cacheService.get('UserConfiguration', defer(() => this.settingsService.getUserConfigurationAsJSON()));
    }

    // #endregion

    constructor(
        @Inject(ESTENSA_MODULE_USER_CONFIGURATION_SETTINGS_SERVICE)
        private settingsService: UserConfigurationSettingsServiceNg1,
        private cacheService: CacheService,
        private httpClient: HttpClient,
        private notificationService: NotificationService,
        private translate: TranslateService,
        private modalNotificationService: ModalDialogNotificationService,
    ) {
        super();
    }

    async initService() {
        if (this._userConfiguration) {
            return this._userConfiguration;
        }

        return firstValueFrom(this.userConfigurationAsync).then((conf) => {
            this._userConfiguration = conf;
            return conf;
        });
    }

    getDefaultConfigurationUserPreferences(): Observable<UserPreferences> {
        return this.httpClient.post<UserPreferences>(
            this.getServerRoot() + '/Settings/GetDefaultConfigurationUserPreferences',
            null,
            this.buildOptions(),
        ) as unknown as Observable<UserPreferences>;
    }

    setUserPreferences(preferences: DeepPartial<UserPreferences>) {
        return this.settingsService
            .setUserPreferences(preferences)
            .then(async () => {
                this.cacheService.delete('UserConfiguration');
                this._userConfiguration = null;
                this._userConfiguration = await this.initService();
                this.modalNotificationService.notify({
                    message: this.translate.instant('UserSettingSaveSuccess'),
                    title: this.translate.instant('SuccessOperation'),
                    type: NotificationType.information,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            })
            .catch((e) => {
                this.notificationService.error(e?.data?.exceptionMessage);
            });
    }

    setDefaultConfigurationUserPreferences(preferences: DeepPartial<UserPreferences>) {
        return this.settingsService
            .setDefaultConfigurationUserPreferences(preferences)
            .then(() => {
                this.cacheService.delete('UserConfiguration');
                this._userConfiguration = merge(this._userConfiguration, preferences);
                this.modalNotificationService.notify({
                    message: this.translate.instant('UserSettingSaveSuccess'),
                    title: this.translate.instant('SuccessOperation'),
                    type: NotificationType.information,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            })
            .catch((e) => {
                this.notificationService.error(e?.data?.exceptionMessage);
            });
    }

    /**
     * Verifica se una o piu' autorizzazioni sono attive sull'utente loggato
     * @param authorizationList: lista di autorizzazioni da verificare
     * @param operator: logica per concatenare le autorizzazioni - valori possibili: '&&' '||' oppure 'single'
     */
    isAuthorized(authorizationList: string[], operator: '||' | '&&' | 'single'): boolean {
        let isAuthorized = false;
        switch (operator) {
            case '||':
                isAuthorized = authorizationList.reduce((previousValue, currentValue) => {
                    return previousValue || this.hasPermission(currentValue);
                }, false);
                break;

            case 'single':
                isAuthorized = this.hasPermission(authorizationList[0]);
                break;

            default:
                // case "&&": - MB CodeSmell rimosso case
                isAuthorized = authorizationList.reduce((previousValue, currentValue) => {
                    return previousValue && this.hasPermission(currentValue);
                }, true);
                break;
        }

        return isAuthorized;
    }

    private hasPermission(permission: string): boolean {
        return this._userConfiguration?.authorizations[permission] === true;
    }
}
